import type { CSSProperties, ReactNode } from "react";
import styles from "./ds.module.css";

/** The one bordered surface pages are built out of: white, 1px hairline, 10px radius. */
export function Card({ title, count, actions, padded = false, clip = true, children, style, className }: {
    title?: ReactNode;
    count?: number | null;
    actions?: ReactNode;
    padded?: boolean;
    /** Off for a card whose popovers must overflow it. */
    clip?: boolean;
    children?: ReactNode;
    style?: CSSProperties;
    className?: string;
}) {
    return (
        <div className={`${styles.card} ${clip ? styles.cardClip : ""} ${className ?? ""}`} style={style}>
            {(title || actions) && (
                <div className={styles.cardHead}>
                    {title && <span className={styles.cardTitle}>{title}</span>}
                    {count != null && <span className={styles.count}>{count.toLocaleString("en-US")}</span>}
                    {actions && <div className={styles.cardActions}>{actions}</div>}
                </div>
            )}
            <div className={padded ? styles.cardPadded : undefined}>{children}</div>
        </div>
    );
}

/**
 * One card shell for every dashboard widget, so headers match. An empty widget collapses to
 * a one-line "No data" row instead of a large blank card; a loading one shows a skeleton.
 */
export function WidgetCard({ title, count, meta, infoTip, actions, isEmpty = false, emptyLabel = "No data for this launch", loading = false, children, style }: {
    title: ReactNode;
    count?: number | null;
    meta?: ReactNode;
    infoTip?: string;
    actions?: ReactNode;
    isEmpty?: boolean;
    emptyLabel?: ReactNode;
    loading?: boolean;
    children?: ReactNode;
    style?: CSSProperties;
}) {
    if (isEmpty && !loading) {
        return (
            <div className={`${styles.card} ${styles.cardClip}`} style={style}>
                <div className={styles.cardHead} style={{ borderBottom: 0 }}>
                    <span className={styles.cardTitle}>{title}</span>
                    <span className={styles.cardEmptyNote}><i className="bi bi-dash-circle" aria-hidden="true" />{emptyLabel}</span>
                </div>
            </div>
        );
    }
    return (
        <div className={`${styles.card} ${styles.cardClip}`} style={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", ...style }}>
            <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{title}</span>
                {count != null && <span className={styles.count}>{count.toLocaleString("en-US")}</span>}
                <div className={styles.cardActions}>
                    {meta && <span className={styles.cardMeta}>{meta}</span>}
                    {infoTip && <i className="bi bi-info-circle" title={infoTip} style={{ color: "var(--slate-400)", fontSize: 13, cursor: "help" }} />}
                    {actions}
                </div>
            </div>
            {loading ? <SkeletonLines /> : <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</div>}
        </div>
    );
}

export function SkeletonLines() {
    return (
        <div aria-busy="true" style={{ flex: 1, minHeight: 112, padding: "16px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div className={`${styles.skeleton} ${styles.skeletonDark}`} style={{ height: 12, width: "58%" }} />
            <div className={styles.skeleton} style={{ height: 10, width: "86%" }} />
            <div className={styles.skeleton} style={{ height: 10, width: "72%" }} />
            <div className={styles.skeleton} style={{ height: 10, width: "40%" }} />
        </div>
    );
}

export function Skeleton({ width, height = 10, dark = false, style }: { width: number | string; height?: number; dark?: boolean; style?: CSSProperties }) {
    return <div className={`${styles.skeleton} ${dark ? styles.skeletonDark : ""}`} style={{ width, height, ...style }} />;
}

export function ErrorNote({ children }: { children: ReactNode }) {
    return <div className={styles.alert} role="alert">{children}</div>;
}
